import React, { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { useFormContext, useFieldArray } from 'react-hook-form'

import { Alert } from 'antd'
import produce from 'immer'
import { DateTime } from 'luxon'
import styled from 'styled-components'

import { sizes, colours } from '@qflow/theme'

import {
  FormInput,
  FormRules,
  FormItem,
  FormArrayItem,
  ErrorContainer
} from 'portal/components/RecordEditing'
import { FormInfraction } from 'portal/components/RecordEditing/FormInfraction'

import { DateFormatFull } from 'portal/lib/functions'
import {
  CheckDate1ObjisBeforeDate2String,
  CheckHeightLengthWidth,
  CheckProductCertification,
  CheckProductName,
  CheckProductUsedAt,
  CheckQuantity,
  CheckVolume,
  CheckWeight
} from 'portal/lib/functions/SimpleDataValidation'
import { Spacer, Input, ColourButton, Flex, icons } from 'portal/lib/primitives'
import { dateValidator } from 'portal/lib/primitives/forms/Validators'
import { useInfractionGetter } from 'portal/store/infractions/InfractionsContext'

export function Delivery({
  isImperial,
  lineItemDataSourceTracking = {},
  currentProductGroup = null
}) {
  const form = useFormContext()

  const getInfraction = useInfractionGetter()
  const pefcClaimInfraction = getInfraction(
    'givenCertifications[TimberRequiresPEFCCertificateInProductGivenCerts]'
  )
  const fscClaimInfraction = getInfraction(
    'givenCertifications[TimberRequiresFSCCertificateInProductGivenCerts]'
  )
  const pefcOrFscClaimInfraction = getInfraction(
    'givenCertifications[TimberRequiresPEFCOrFSCCertificateInProductGivenCerts]'
  )

  const allProductGroups = useSelector(state => state.productGroups)
  const selectableProductGroups = useMemo(() => {
    const notSet = {
      label: '-- Not set --',
      value: '',
      key: 'not-set'
    }

    const selectableProductGroups = Array.isArray(allProductGroups.data)
      ? [
          notSet,
          ...allProductGroups.data.map(pg => ({
            label: pg.name,
            value: pg.productGroupId ? pg.productGroupId : '',
            key: pg.productGroupId ? pg.productGroupId : ''
          }))
        ]
      : [notSet]

    if (
      currentProductGroup &&
      !selectableProductGroups.find(pg => pg.key === currentProductGroup.key)
    ) {
      selectableProductGroups.splice(1, 0, currentProductGroup)
    }

    return selectableProductGroups
  }, [allProductGroups.data, currentProductGroup])

  const projectTimeZone = useSelector(
    state => state.projectDetails.modules?.timeZone
  )
  const productGroupFields = useFieldArray({
    name: 'productGroups'
  })

  const deliveryArrivalDate = useSelector(
    state => state.records.details.item.data.deliveryArrivalDate
  )
  const deliveryExitDate = useSelector(
    state => state.records.details.item.data.deliveryExitDate
  )

  const calculatedVolume = form.watch('calculatedVolume')
  const chosenPG = form.watch('productGroups')
  const estimatedWeight = form.watch('estimatedWeight')

  const givenCertificationFields = useFieldArray({
    name: 'givenCertifications'
  })

  const VolumeLabelBody = !isImperial ? (
    <>
      Volume (m<sup>3</sup>)
    </>
  ) : (
    'Volume'
  )

  const WeightLabelBody = !isImperial ? 'Weight (t)' : 'Weight'

  return (
    <>
      <FormInput.Hidden name="id" />
      <FormItem
        label="Name"
        valueAdd={{ fieldName: 'name', ...lineItemDataSourceTracking?.name }}
      >
        <FormInput.Text
          label="Name"
          name="name"
          autoFocus
          rules={{
            required: 'Name is required'
          }}
          simpleDataValidationRules={CheckProductName}
        />
      </FormItem>
      {form.formState.dirtyFields.name && (
        <FormItem>
          <Alert
            message="Name has been changed, check Product group is still valid."
            type="warning"
            style={{ padding: '0.5rem 0.75rem' }}
          />
        </FormItem>
      )}
      <FormArrayItem
        label="Product group"
        name="productGroups"
        fieldArray={productGroupFields}
        maxItems={1}
        defaultValue={{ value: '' }}
        render={(field, index) => (
          <FormInput.Dropdown
            name={`productGroups[${index}].value`}
            defaultValue={field.value}
            items={selectableProductGroups}
            placeholder="Select product group"
            simpleDataValidationRules={v => {
              if (v !== currentProductGroup?.value) {
                return [
                  'Warning: Updating product group will remove any estimates and calculated carbon.'
                ]
              }
              return []
            }}
          />
        )}
      />
      <Spacer.Small />
      <FormItem
        label="Produced at"
        valueAdd={{
          fieldName: 'productionTime',
          ...lineItemDataSourceTracking?.productionTime
        }}
      >
        <FormInput.DateTimeCells
          name="productionTime"
          rules={dateValidator(form.getValues, 'productionTime')}
          simpleDataValidationRules={v =>
            CheckDate1ObjisBeforeDate2String(
              'Produced at',
              form.getValues('productionTime'),
              `Arrival date and time (${DateTime.fromISO(deliveryArrivalDate, {
                zone: 'local'
              }).toLocaleString(DateFormatFull(projectTimeZone), {
                locale: Intl.DateTimeFormat().resolvedOptions().locale
              })})`,
              deliveryArrivalDate
            )
          }
        />
      </FormItem>
      <FormItem
        label="Used at"
        valueAdd={{
          fieldName: 'usedAtTime',
          ...lineItemDataSourceTracking?.usedAtTime
        }}
      >
        <FormInput.DateTimeCells
          name="usedAtTime"
          rules={dateValidator(form.getValues, 'usedAtTime')}
          simpleDataValidationRules={v =>
            CheckProductUsedAt(
              'Produced at',
              form.getValues('productionTime'),
              'Used at',
              form.getValues('usedAtTime'),
              `Exit date and time (${DateTime.fromISO(deliveryExitDate, {
                zone: 'local'
              }).toLocaleString(DateFormatFull(projectTimeZone), {
                locale: Intl.DateTimeFormat().resolvedOptions().locale
              })})`,
              deliveryExitDate
            )
          }
        />
      </FormItem>
      <Spacer.Small />
      {!isImperial && (
        <FormItem
          label="Height (m)"
          valueAdd={{
            fieldName: 'height',
            ...lineItemDataSourceTracking?.height
          }}
        >
          <MultiInputWrapper>
            <FormInput.Text
              label="Height"
              name="height"
              type="number"
              rules={FormRules.OptionalPositiveDecimal()}
              simpleDataValidationRules={CheckHeightLengthWidth}
            />
          </MultiInputWrapper>
        </FormItem>
      )}
      {isImperial && (
        <FormItem
          label="Height"
          valueAdd={{
            fieldName: 'height',
            ...lineItemDataSourceTracking?.height
          }}
        >
          <MultiInputWrapper>
            <FormInput.Text
              name="heightFoot"
              type="number"
              rules={FormRules.OptionalPositiveOrZeroDecimal()}
              inputUnit="foot"
            />
            <FormInput.Text
              name="heightInch"
              type="number"
              rules={FormRules.OptionalPositiveOrZeroDecimal()}
              inputUnit="inch"
            />
          </MultiInputWrapper>
        </FormItem>
      )}

      {!isImperial && (
        <FormItem
          label="Width (m)"
          valueAdd={{
            fieldName: 'width',
            ...lineItemDataSourceTracking?.width
          }}
        >
          <MultiInputWrapper>
            <FormInput.Text
              label="Width"
              name="width"
              type="number"
              rules={FormRules.OptionalPositiveDecimal()}
              simpleDataValidationRules={CheckHeightLengthWidth}
            />
          </MultiInputWrapper>
        </FormItem>
      )}
      {isImperial && (
        <FormItem
          label="Width"
          valueAdd={{
            fieldName: 'width',
            ...lineItemDataSourceTracking?.width
          }}
        >
          <MultiInputWrapper>
            <FormInput.Text
              name="widthFoot"
              type="number"
              rules={FormRules.OptionalPositiveOrZeroDecimal()}
              inputUnit="foot"
            />
            <FormInput.Text
              name="widthInch"
              type="number"
              rules={FormRules.OptionalPositiveOrZeroDecimal()}
              inputUnit="inch"
            />
          </MultiInputWrapper>
        </FormItem>
      )}

      {!isImperial && (
        <FormItem
          label="Length (m)"
          valueAdd={{
            fieldName: 'length',
            ...lineItemDataSourceTracking?.length
          }}
        >
          <MultiInputWrapper>
            <FormInput.Text
              label="Length"
              name="length"
              type="number"
              rules={FormRules.OptionalPositiveDecimal()}
              simpleDataValidationRules={CheckHeightLengthWidth}
            />
          </MultiInputWrapper>
        </FormItem>
      )}
      {isImperial && (
        <FormItem
          label="Length"
          valueAdd={{
            fieldName: 'length',
            ...lineItemDataSourceTracking?.length
          }}
        >
          <MultiInputWrapper>
            <FormInput.Text
              name="lengthFoot"
              type="number"
              rules={FormRules.OptionalPositiveOrZeroDecimal()}
              inputUnit="foot"
            />
            <FormInput.Text
              name="lengthInch"
              type="number"
              rules={FormRules.OptionalPositiveOrZeroDecimal()}
              inputUnit="inch"
            />
          </MultiInputWrapper>
        </FormItem>
      )}
      <Spacer.Small />
      <FormItem
        label={VolumeLabelBody}
        valueAdd={{
          fieldName: 'givenVolume',
          ...lineItemDataSourceTracking?.givenVolume
        }}
      >
        <MultiInputWrapper>
          <FormInput.Text
            label="Volume"
            name="givenVolume"
            type="number"
            placeholder={calculatedVolume ? `est. ${calculatedVolume}` : ''}
            rules={FormRules.OptionalPositiveDecimal()}
            simpleDataValidationRules={v =>
              CheckVolume(v, isImperial ? 'CubicYard' : 'CubicMeter')
            }
            inputUnit={isImperial ? 'cubic yard' : null}
          />
        </MultiInputWrapper>
      </FormItem>
      <FormItem
        label={WeightLabelBody}
        valueAdd={{
          fieldName: 'weight',
          ...lineItemDataSourceTracking?.weight
        }}
      >
        <MultiInputWrapper>
          <FormInput.Text
            label="Weight"
            name="weight"
            type="number"
            placeholder={estimatedWeight ? `est. ${estimatedWeight}` : ''}
            rules={FormRules.OptionalPositiveDecimal()}
            simpleDataValidationRules={v =>
              CheckWeight(v, isImperial ? 'Pound' : 'Tonne')
            }
            inputUnit={isImperial ? 'lbs' : null}
          />
        </MultiInputWrapper>
      </FormItem>
      <Spacer.Small />
      <FormItem
        label="Quantity (units)"
        valueAdd={{
          fieldName: 'quantity',
          ...lineItemDataSourceTracking?.quantity
        }}
      >
        <MultiInputWrapper>
          <FormInput.Text
            label="Quantity"
            name="quantity"
            type="number"
            rules={FormRules.OptionalPositiveDecimal()}
            simpleDataValidationRules={CheckQuantity}
          />
        </MultiInputWrapper>
      </FormItem>
      <FormItem label="RefId" hidden={true}>
        <FormInput.Text name="referenceProductId" />
      </FormItem>
      <Spacer.Small />
      {Array.isArray(chosenPG) &&
        chosenPG.some(
          pg => pg.value === '93197abb-2481-4f2f-a86e-786e4dff11d3'
        ) && ( // TIMBER
          <>
            <FormArrayItem
              label="Timber Rating"
              name="givenCertifications"
              valueAdd={{
                fieldName: 'givenCertifications',
                ...lineItemDataSourceTracking?.givenCertifications
              }}
              fieldArray={givenCertificationFields}
              defaultValue={{ value: '' }}
              render={(field, index, fieldArray) => (
                <>
                  <Flex.Row>
                    <FormInput.Text
                      label="Timber Rating"
                      name={`givenCertifications[${index}].value`}
                      defaultValue={field.value}
                      type="string"
                      simpleDataValidationRules={CheckProductCertification}
                    />

                    <Spacer.Fine />

                    <ColourButton.BasicBorderless
                      onClick={() => {
                        fieldArray.remove(index)
                      }}
                      style={{
                        color: colours.CORE_NAVY,
                        marginTop: '0.125rem'
                      }}
                    >
                      <icons.TrashCan size={sizes.SMALL} />
                    </ColourButton.BasicBorderless>
                  </Flex.Row>

                  <Spacer.Tiny />
                </>
              )}
            ></FormArrayItem>
            <FormItem>
              {fscClaimInfraction && (
                <ErrorContainer>
                  <FormInfraction infraction={fscClaimInfraction} />
                </ErrorContainer>
              )}
              {pefcClaimInfraction && (
                <ErrorContainer>
                  <FormInfraction infraction={pefcClaimInfraction} />
                </ErrorContainer>
              )}
              {pefcOrFscClaimInfraction && (
                <ErrorContainer>
                  <FormInfraction infraction={pefcOrFscClaimInfraction} />
                </ErrorContainer>
              )}
            </FormItem>
          </>
        )}
    </>
  )
}

Delivery.transformToFormData = (item, projectTimeZone) => {
  return produce(item, draft => {
    draft.productionTime =
      Input.DateTimeCells.getDateTimeInputValuesFromISOString(
        item.productionTime,
        projectTimeZone
      )
    draft.usedAtTime = Input.DateTimeCells.getDateTimeInputValuesFromISOString(
      item.usedAtTime,
      projectTimeZone
    )
    draft.givenCertifications = (draft.givenCertifications ?? [])
      .filter(Boolean)
      .map(cert => ({ value: cert }))
    draft.productGroups = (draft.productGroups ?? [])
      .filter(Boolean)
      .map(pg => ({ value: pg.productGroupId }))
  })
}

Delivery.transformFromFormData = (
  formItem,
  projectTimeZone,
  isImperial,
  allProductGroups = { data: [] },
  currentProductGroup = null
) => {
  const getProductGroupName = productGroupId => {
    // First look in all product groups
    let productGroupName = allProductGroups.data.find(
      pg => pg.productGroupId === productGroupId
    )?.name
    // (For older Product Groups) If not found, look in the original data
    if (!productGroupName && Array.isArray(currentProductGroup)) {
      productGroupName = currentProductGroup.find(
        pg => pg.productGroupId === productGroupId
      )?.name

      // If still not found, check again using value instead of productGroupId
      if (!productGroupName) {
        productGroupName = currentProductGroup.find(
          pg => pg.value === productGroupId
        )?.name
      }
    } else if (
      !productGroupName &&
      currentProductGroup.value === productGroupId
    ) {
      productGroupName = currentProductGroup.name
    }
    return productGroupName
  }
  return produce(formItem, draft => {
    draft.productionTime = Input.DateTimeCells.getISOStringFromValues(
      formItem.productionTime,
      projectTimeZone
    )
    draft.usedAtTime = Input.DateTimeCells.getISOStringFromValues(
      formItem.usedAtTime,
      projectTimeZone
    )

    // If isImperial merge Height, Width and Length fields in to their own arrays
    if (isImperial) {
      draft.height = []
      if (formItem.heightFoot) {
        draft.height.push({ value: formItem.heightFoot, unit: 'Foot' })
      }
      if (formItem.heightInch) {
        draft.height.push({ value: formItem.heightInch, unit: 'Inch' })
      }

      draft.width = []
      if (formItem.widthFoot) {
        draft.width.push({ value: formItem.widthFoot, unit: 'Foot' })
      }
      if (formItem.widthInch) {
        draft.width.push({ value: formItem.widthInch, unit: 'Inch' })
      }

      draft.length = []
      if (formItem.lengthFoot) {
        draft.length.push({ value: formItem.lengthFoot, unit: 'Foot' })
      }
      if (formItem.lengthInch) {
        draft.length.push({ value: formItem.lengthInch, unit: 'Inch' })
      }

      // And do similar for Volume and Weight fields
      if (formItem.calculatedVolume) {
        draft.calculatedVolume = [
          { value: formItem.calculatedVolume, unit: 'CubicYard' }
        ]
      } else {
        draft.calculatedVolume = []
      }

      if (formItem.givenVolume) {
        draft.givenVolume = [{ value: formItem.givenVolume, unit: 'CubicYard' }]
      } else {
        draft.givenVolume = []
      }

      if (formItem.estimatedWeight) {
        draft.estimatedWeight = [
          { value: formItem.estimatedWeight, unit: 'Pound' }
        ]
      } else {
        draft.estimatedWeight = []
      }

      if (formItem.weight) {
        draft.weight = [{ value: formItem.weight, unit: 'Pound' }]
      } else {
        draft.weight = []
      }
    }
    draft.givenCertifications = (formItem.givenCertifications ?? [])
      .filter(Boolean)
      .map(cert => cert.value)
    draft.productGroups = (formItem.productGroups ?? [])
      .filter(Boolean)
      .filter(pg => pg.value !== 'not-set')
      .map(pg => {
        return {
          productGroupId: pg.value,
          name: getProductGroupName(pg.value)
        }
      })
  })
}

export const MultiInputWrapper = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  min-width: 15rem;
  gap: 0 0.5rem;
  flex: 0 1 auto;
`
